import { ForwardedRef, ReactNode, forwardRef } from 'react';
import { Button, IconButton, SxProps } from '@mui/material';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { useFormContext } from 'react-hook-form';
import { Import } from 'mdi-material-ui';
import { useAdditionalDataPanel } from 'src/@core/hooks/useAdditionalDataPanel';
import { useAdditionalDataTransferObject } from 'src/@core/hooks/useAdditionalDataTransferObject';
import { VigoTooltip } from 'src/@vigo/vigo-tooltip';
import { useConfirmDialog } from 'src/@core/hooks/useConfirmDialog';

export interface ImportButtonProps {
  dtoId?: string;
  sx?: SxProps;
  variant?: 'text' | 'outlined' | 'contained';
  title?: string;
  icon?: ReactNode;
  onAccept?: (handleClose: () => void) => void;
  onCancel?: () => void;
  toolTipText?: string;
  confirmationMessage?: string;
}

const ImportButton = (
  {
    dtoId,
    sx = {},
    variant = 'outlined',
    title,
    icon = <Import />,
    onAccept,
    onCancel,
    toolTipText = '',
    confirmationMessage
  }: ImportButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) => {
  const prod = useGetProductLevel();

  const { control } = useFormContext();

  const { togglePanel, closeAnyOpenPanels, setSidePanelClosed } = useAdditionalDataPanel();

  const { set } = useAdditionalDataTransferObject();
  
  const { set: confirm } = useConfirmDialog();

  const handleOpen = async () => {
    // Dont open if dtoId is not available (unless we have custom handlers)
    if (!dtoId && !onAccept) {

      return;
    }
    
    // Show confirmation dialog if message is provided
    if (confirmationMessage) {
      const confirmed = await confirm(confirmationMessage);
      if (!confirmed) {

        return;
      }
    }
    
    // If we have custom accept handler but no dtoId, just call the handlers directly
    if (!dtoId && onAccept) {
      handleImport();
      
      return;
    }
    
    set('importDtoId', dtoId);
    set('importPanelFormMethods', { handleImport, handleCancel });
    togglePanel(`importPanel-${dtoId}`);
  };

  /**
   * Resets the form to its default values.
   */
  function handleCancel() {
    closeAnyOpenPanels();
    setSidePanelClosed(true);
    onCancel?.();
  }

  /**
   * Handles the import button click.
   * @returns A function that handles the form submission.
   */
  function handleImport() {
    onAccept?.(() => handleCancel());
  }

  if (!control || (prod !== Products.MyPortal && prod !== Products.Flow)) {
    return null;
  }
  
  // Don't render the button if dtoId is not available (unless we have custom handlers)
  if (!dtoId && !onAccept) {
    return null;
  }

  return (
    <>
      {!title && (
        <VigoTooltip title={toolTipText}>
          <IconButton ref={ref} aria-label="Import" onClick={handleOpen} sx={{ ...sx }} size="small">
            {icon}
          </IconButton>
        </VigoTooltip>
      )}

      {title && (
        <VigoTooltip title={toolTipText}>
          <Button ref={ref} variant={variant} sx={{ ...sx }} startIcon={icon} onClick={handleOpen}>
            {title}
          </Button>
        </VigoTooltip>
      )}
    </>
  );
};

export default forwardRef(ImportButton);
